"use client";

import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog";
import { Textarea } from "./textarea";
// #13715: the tsconfig path (libs/ui/brand-kit/components/src/index.ts), not the package link, so the
// merged tree resolves its own brand-kit source.
import { setDiscardConfirm, type DISCARD_CHANGES_TEXT } from "@smallbiz/brand-kit/components";

export interface ConfirmDialogOptions {
  /** Heading. Defaults to "Are you sure?". */
  title?: string;
  /** Body text / node. */
  description?: React.ReactNode;
  /** Confirm button label. Defaults to "Confirm". */
  confirmText?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelText?: string;
  /** Style the confirm button as a destructive action (red). */
  destructive?: boolean;
}

export interface PromptDialogOptions extends ConfirmDialogOptions {
  /** Textarea placeholder. */
  placeholder?: string;
  /** Longest text the textarea takes. */
  maxLength?: number;
  /** Disable the confirm button while the trimmed text is empty. */
  required?: boolean;
  /** #13655: the text the textarea starts with (a rename shows the current name). */
  defaultValue?: string;
}

type PromptInput = Pick<PromptDialogOptions, "placeholder" | "maxLength" | "required" | "defaultValue">;

/** #13655: one choice in a {@link choiceDialog}. */
export interface ChoiceDialogOption {
  /** The value the dialog resolves when this choice is picked. */
  value: string;
  /** The button text. */
  label: string;
  /** Muted text after the label (for example the linked team names). */
  hint?: string;
}

export interface ChoiceDialogOptions extends Omit<ConfirmDialogOptions, "confirmText" | "destructive"> {
  /** The choices, in order. */
  options: ChoiceDialogOption[];
}

type ConfirmRequest = ConfirmDialogOptions & {
  id: number;
  /** Set by {@link promptDialog}: the host shows a textarea. */
  input?: PromptInput;
  /** Set by {@link choiceDialog}: the host shows one button per choice and no Confirm. */
  choices?: ChoiceDialogOption[];
  resolve: (value: boolean, text?: string) => void;
};

// Module-level singleton store (same imperative model as sonner's toast):
// `confirmDialog()` publishes a request, <ConfirmDialogHost/> renders it.
let current: ConfirmRequest | null = null;
let counter = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}
function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
function getSnapshot() {
  return current;
}

/**
 * Imperative, promise-based confirmation dialog — the canonical replacement for
 * blocking native `confirm()` (decision:
 * DESIGN.md "Toasts" / EPIC #5032 B-6).
 *
 * Requires `<ConfirmDialogHost />` mounted once at the app root (it is, next to
 * the canonical `<Toaster />`).
 *
 * @example
 * ```ts
 * if (!(await confirmDialog("Delete this item? This cannot be undone."))) return;
 * // ...proceed with delete
 * ```
 * @example
 * ```ts
 * const ok = await confirmDialog({
 *   title: "Delete file?",
 *   description: name,
 *   confirmText: "Delete",
 *   destructive: true,
 * });
 * ```
 */
export function confirmDialog(options: string | ConfirmDialogOptions): Promise<boolean> {
  const opts: ConfirmDialogOptions =
    typeof options === "string" ? { description: options } : options;

  // No host mounted → fall back to native window.confirm() so callers always resolve.
  if (listeners.size === 0) {
    const msg =
      typeof opts.description === "string"
        ? opts.description
        : (opts.title ?? "Are you sure?");
    return Promise.resolve(typeof window !== "undefined" ? window.confirm(msg) : false);
  }

  return new Promise<boolean>((resolve) => publish({ ...opts, resolve: (ok) => resolve(ok) }));
}

function publish(req: Omit<ConfirmRequest, "id">) {
  // Cancel any in-flight request before replacing it so the previous promise
  // doesn't hang forever (e.g. from a double-click).
  if (current) {
    const prev = current;
    current = null;
    prev.resolve(false);
  }
  current = { id: ++counter, ...req };
  emit();
}

/**
 * #11093 A2: promise-based text prompt on the same host as {@link confirmDialog}.
 * Resolves the trimmed text on confirm, or null on cancel (and on an empty
 * text when `required`). With no host mounted it falls back to `window.prompt()`.
 *
 * @example
 * ```ts
 * const reason = await promptDialog({ title: "Why cancel?", required: true, maxLength: 1000 });
 * if (reason === null) return;
 * ```
 */
export function promptDialog(options: PromptDialogOptions): Promise<string | null> {
  const { placeholder, maxLength, required, defaultValue, ...opts } = options;
  const finish = (text: string | null | undefined): string | null => {
    const trimmed = (text ?? "").trim().slice(0, maxLength ?? undefined);
    return required && !trimmed ? null : trimmed;
  };

  if (listeners.size === 0) {
    if (typeof window === "undefined") return Promise.resolve(null);
    const text = window.prompt(opts.title ?? "Enter a value", defaultValue);
    return Promise.resolve(text === null ? null : finish(text));
  }

  return new Promise<string | null>((resolve) =>
    publish({
      ...opts,
      input: { placeholder, maxLength, required, defaultValue },
      resolve: (ok, text) => resolve(ok ? finish(text) : null),
    }),
  );
}

/**
 * #13655: promise-based pick-one list on the same host as {@link confirmDialog}. Resolves the
 * picked option's value, or null on cancel. It replaces a native prompt that asked for a number
 * from a list. With no host mounted it falls back to {@link promptDialog} with a numbered list.
 *
 * @example
 * ```ts
 * const channelId = await choiceDialog({
 *   title: "Choose a Slack channel",
 *   options: channels.map((c) => ({ value: c.id, label: `#${c.name}` })),
 * });
 * if (channelId === null) return;
 * ```
 */
export function choiceDialog(options: ChoiceDialogOptions): Promise<string | null> {
  const { options: choices, ...opts } = options;
  if (choices.length === 0) return Promise.resolve(null);

  if (listeners.size === 0) {
    const list = choices.map((c, i) => `${i + 1}. ${c.label}${c.hint ? ` ${c.hint}` : ""}`).join("\n");
    return promptDialog({ title: `${opts.title ?? "Choose one"}:\n${list}\n\nEnter number:` }).then((text) => {
      const i = text === null ? Number.NaN : Number.parseInt(text, 10) - 1;
      return choices[i]?.value ?? null;
    });
  }

  return new Promise<string | null>((resolve) =>
    publish({ ...opts, choices, resolve: (ok, value) => resolve(ok && value !== undefined ? value : null) }),
  );
}

function settle(value: boolean, text?: string) {
  const req = current;
  current = null;
  emit();
  req?.resolve(value, text);
}

/**
 * Host for {@link confirmDialog}, {@link promptDialog} and {@link choiceDialog}. Mount exactly
 * once at the app root.
 */
export function ConfirmDialogHost() {
  const req = React.useSyncExternalStore(subscribe, getSnapshot, () => null);
  const open = req !== null;
  const [text, setText] = React.useState("");
  const reqId = req?.id;
  const startText = req?.input?.defaultValue ?? "";
  React.useEffect(() => {
    setText(startText);
  }, [reqId, startText]);
  const blocked = Boolean(req?.input?.required) && !text.trim();
  // #13715: the brand-kit Dialog (re-exported by @sbs/ui-core) asks "Discard changes?"
  // through this styled confirm while the host is mounted. brand-kit cannot import this
  // module (a cycle), so the host registers it.
  React.useEffect(() => setDiscardConfirm((options: typeof DISCARD_CHANGES_TEXT) => confirmDialog(options)), []);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // Escape / programmatic close counts as cancel.
        if (!next && current) settle(false);
      }}
    >
      {/* #13845: an explicit cap. The brand-kit dialog this replaced set an inline maxWidth; with the
          class alone the "Discard changes?" confirm rendered 1317 px wide on a 1349 px window. */}
      <AlertDialogContent data-testid="confirm-dialog" style={{ maxWidth: "min(32rem, calc(100% - 2rem))" }}>
        <AlertDialogHeader>
          <AlertDialogTitle>{req?.title ?? "Are you sure?"}</AlertDialogTitle>
          {req?.description != null && (
            <AlertDialogDescription>{req.description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {req?.input && (
          <Textarea
            autoFocus
            aria-label={req.title ?? "Text"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={req.input.placeholder}
            maxLength={req.input.maxLength}
            rows={3}
          />
        )}
        {req?.choices && (
          <div role="listbox" aria-label={req.title ?? "Choices"} className="flex max-h-72 flex-col gap-1 overflow-y-auto">
            {req.choices.map((c) => (
              <button
                key={c.value}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => settle(true, c.value)}
                className="rounded-md border px-3 py-2 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {c.label}
                {c.hint && <span className="ml-1 text-muted-foreground">{c.hint}</span>}
              </button>
            ))}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => settle(false)}>
            {req?.cancelText ?? "Cancel"}
          </AlertDialogCancel>
          {!req?.choices && (
            <AlertDialogAction
              disabled={blocked}
              onClick={() => settle(true, text)}
              className={
                req?.destructive
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : undefined
              }
            >
              {req?.confirmText ?? "Confirm"}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
